import eq from "lodash-es/eq"
import isNull from "lodash-es/isNull"
import { useIsomorphicLayoutEffect, usePrevious } from "react-use"

import { reduxBatch } from "@/3514/utils"
import type { TEmptyAsyncCallback } from "@/shared/types/functions"

import { useCoachingChatConversationQueryRefetch as useConversationRefetch } from "../api"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationStatus as EConversationStatus,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"

function useCoachingChatApiSwitchReloader({ entityId }: Pick<_TSharedParams, "entityId">): void {
    const apiMode: EConversationApiMode = useConversationApiMode()

    const prevApiMode: EConversationApiMode = usePrevious<EConversationApiMode>(apiMode)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const conversationId: number = useConversationIdSelector(entityId)

    const dispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationReFetcher: TEmptyAsyncCallback = useConversationRefetch(conversationId)

    useIsomorphicLayoutEffect((): void => {
        if (eq(apiMode, EConversationApiMode.Rest) && eq(prevApiMode, EConversationApiMode.Sockets)) {
            void conversationReFetcher()

            reduxBatch(
                (): void => (
                    dispatcher.clearError(),
                    dispatcher.loadConversation({
                        completedAt: undefined,
                        id: conversationId,
                        status: isNull(conversationId) ? conversationStatus : EConversationStatus.InProgress,
                        isMatrixFeedbackCompleted: false,
                        summary: [],
                        messages: []
                    })
                )
            )
        }
    }, [apiMode, prevApiMode, conversationId, conversationStatus, conversationReFetcher])
}

export { useCoachingChatApiSwitchReloader }
